import { Form, Grid, SelectBox } from '@domain-group/suit-theme'

import EntitySelectBox from '@components/EntitySelectBox'
import EntitySelector from '@components/EntitySelector'

import useCampaignStatuses from '@hooks/useCampaignStatuses'
import useCurrentUser from '@hooks/useCurrentUser'
import type { UseReportingPayload } from '@hooks/useReporting'

import type { ReportItem } from '@functions/reporting'

import type { CampaignModel } from '@domain-group/realhub-backend-api-types'

const Reporting = (props: { isSidebar?: boolean, reportingPayload: UseReportingPayload }) => {
  const { isSidebar, reportingPayload } = props

  const {
    callbacks: {
      setState,
      shouldRenderField,
    },
    initReport,
    reportingForm: {
      entityState, errors, handlers, setEntityState,
    },
    reportItems,
    selectedReportItemKey,
    shouldShowInvoiceDate,
    shouldShowSoldDate,
    strings,
  } = reportingPayload

  const {
    agency_id,
    brand_id,
    campaign_id,
    campaign_status,
    date_column,
    email,
    end_date,
    provider_id,
    publication_id,
    role_id,
    start_date,
    string,
    user_id,
  } = entityState

  const { isAgencyAdmin, isBrandUser, isProviderUser } = useCurrentUser()
  const campaignStatuses = useCampaignStatuses({ performHttpRequests: isAgencyAdmin || isBrandUser })

  const { filteredCampaignStatuses, hasFilteredCampaignStatuses, loading } = campaignStatuses

  return (
    <Grid
      // @ts-expect-error TS2322
      gridColumnGap="medium"
      gridRowGap="large"
      gridAutoRows="1fr"
      gridTemplateColumns={isSidebar ? 'repeat(1, 1fr)' : ['repeat(1, 1fr)', 'repeat(2, 1fr)']}
      padding={isSidebar ? 0 : 'large'}
    >
      <Form.Field label="* Report Type" boxProps={{ gridColumn: [null, isSidebar ? null : 'span 2'] }}>
        <SelectBox
          callbacks={{
            selectItem: (item: ReportItem) => setState({ isSetup: false, selectedReportItemKey: item.key }),
          }}
          items={reportItems}
          selectedItemKey={selectedReportItemKey}
          style={{ height: 37, zIndex: 3 }}
          ulStyle={{ maxHeight: 300, overflow: 'auto' }}
        />
      </Form.Field>

      {shouldRenderField('start_date') && (
        <Form.Field errorMessage={errors.start_date} label="* Start Date">
          <input name="start_date" type="date" value={start_date || ''} {...handlers} />
        </Form.Field>
      )}

      {shouldRenderField('end_date') && (
        <Form.Field errorMessage={errors.end_date} label="* End Date">
          <input
            data-validate-field-on="change"
            min={start_date}
            name="end_date"
            type="date"
            value={end_date || ''}
            {...handlers}
          />
        </Form.Field>
      )}

      {shouldRenderField('date_column') && (
        <Form.Field label="* Date Type">
          {/*
           // @ts-expect-error TS2322 */}
          <select value={date_column} name="date_column" {...handlers}>
            <option value="approval_date">Approval Date</option>

            {shouldShowInvoiceDate && <option value="invoice_date">Invoice Date</option>}
            {shouldShowSoldDate && <option value="sold_date">Sold Date</option>}
          </select>
        </Form.Field>
      )}

      {shouldRenderField('campaign_status') && !loading && hasFilteredCampaignStatuses && (
        <Form.Field label="* Campaign Status">
          {/*
           // @ts-expect-error TS2322 */}
          <select
            value={campaign_status}
            name="campaign_status"
            {...handlers}
          >
            <option value="all">All</option>
            {filteredCampaignStatuses?.map((status) => {
              // @ts-expect-error TS2339
              const { short_name, title } = status

              return (
                <option key={short_name} value={short_name}>
                  {title}
                </option>
              )
            })}
          </select>
        </Form.Field>
      )}

      {shouldRenderField('string') && (
        <Form.Field
          errorMessage={errors.string}
          // @ts-expect-error TS2339
          label={strings.labels?.string || 'Filter by Title'}
        >
          <input name="string" type="text" value={string} {...handlers} />
        </Form.Field>
      )}

      {shouldRenderField('brand_id')
        && !isBrandUser
        && !isAgencyAdmin
        && (
          <EntitySelector
            callbacks={{
              selectItem: brand => setEntityState({ brand_id: brand ? brand.id : null }),
            }}
            disabled={!!initReport.brand_id}
            entityKey="brands"
            errorMessage={errors.brand_id}
            label="Brand"
            selectedItemId={brand_id}
          />
        )}

      {shouldRenderField('agency_id') && !isAgencyAdmin && (
        <EntitySelector
          callbacks={{
            selectItem: agency => setEntityState({ agency_id: agency ? agency.id : null }),
          }}
          disabled={!!initReport.agency_id}
          entityKey="agencies"
          errorMessage={errors.agency_id}
          label="Agency"
          searchRequestOptions={{ brand_id }}
          selectedItemId={agency_id}
        />
      )}

      {shouldRenderField('provider_id') && !isProviderUser && (
        <EntitySelector
          callbacks={{
            selectItem: provider => setEntityState({ provider_id: provider ? provider.id : null }),
          }}
          disabled={!!initReport.provider_id}
          entityKey="providers"
          errorMessage={errors.provider_id}
          // @ts-expect-error TS2339
          label={strings?.labels?.provider || 'Provider'}
          selectedItemId={provider_id}
        />
      )}

      {shouldRenderField('publication_id') && (
        <EntitySelector
          callbacks={{
            selectItem: publication => setEntityState({ publication_id: publication ? publication.id : null }),
          }}
          disabled={!!initReport.publication_id || !provider_id}
          entityKey="publications"
          errorMessage={errors.publication_id}
          label="Publication"
          searchRequestOptions={{ provider_id }}
          selectedItemId={publication_id}
        />
      )}

      {shouldRenderField('role_id') && (
        <Form.Field errorMessage={errors.role_id} label="Role">
          <EntitySelectBox
            data-value-type="number"
            entityKey="roles"
            // @ts-expect-error TS2322
            name="role_id"
            value={role_id || ''}
            {...handlers}
          />
        </Form.Field>
      )}

      {shouldRenderField('user_id') && (
        <EntitySelector
          callbacks={{
            selectItem: user => setEntityState({ user_id: user ? user.id : null }),
          }}
          entityKey="users"
          entityTitleKey="full_name"
          errorMessage={errors.user_id}
          label="User"
          searchRequestOptions={{ role_id }}
          selectedItemId={user_id}
        />
      )}

      {shouldRenderField('email') && (
        <Form.Field errorMessage={errors.email} label="Email">
          <input
            data-validate-field-on="change"
            name="email"
            type="email"
            value={email}
            {...handlers}
          />
        </Form.Field>
      )}

      {shouldRenderField('campaign_id') && (
        <EntitySelector
          callbacks={{
            // @ts-expect-error TS2322
            selectItem: (selectedCampaign: CampaignModel) => setEntityState(
              { campaign_id: selectedCampaign ? selectedCampaign.id : null },
            ),
          }}
          entityKey="campaigns"
          errorMessage={errors.campaign_id}
          label="* Campaign"
          marginBottom="large"
          searchRequestOptions={{
            hidden: false,
          }}
          selectedItemId={campaign_id}
        />
      )}
    </Grid>
  )
}

export default Reporting
